import { memo, useMemo } from "react";
import { parseISO } from "date-fns";
import Link from "next/link";
import { PropertyDataV2 } from "commonModule/services/common";
import LazyLoadedImage from "components/slider/LazyLoadedImage";
import TravelAgentPropertyCardBody from "components/travel-agent/TravelAgentPropertyCardBody";
import { FALLBACK_LOGO_IMAGE } from "constant";
import useCurrency from "hooks/swr/useCurrency";
import { useAppSelector } from "stores/hooks";
import { selectSearch, selectSearchQuery } from "stores/search/searchSlice";
import { diffDays } from "utils/dates";
import { getFiltersForMixpanelEvent, getSavedPrice } from "utils/helper";
import { useMediaQuery } from "utils/hooks/useMediaQuery";
import { setDetailPageOrigin, setLastSearchUrl, setPropertyPriceForDetailPageEvent } from "utils/local-storage";
import { buildDetailPageUrl } from "utils/url";
const MultiUnitContent = memo(({
  property,
  shouldDisplayTotalPrice,
  propertyIndex,
  isPriceMarker,
  pageOrigin,
  isMultiUnitProperty = false,
  unitIndex,
  actualPropertyCount
}: {
  property: PropertyDataV2;
  shouldDisplayTotalPrice: boolean;
  propertyIndex?: number;
  isPriceMarker?: boolean;
  pageOrigin: string;
  isMultiUnitProperty: boolean;
  unitIndex?: number;
  actualPropertyCount: number;
}) => {
  const search = useAppSelector(selectSearch);
  const searchQuery = useAppSelector(selectSearchQuery);
  const {
    rate,
    formatter
  } = useCurrency();
  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const numericSavePrice = getSavedPrice({
    shouldDisplayTotalPrice,
    searchQuery,
    property,
    rate,
    formatter,
    addCurrencySymbol: false
  });
  const propertyUrl = useMemo(() => buildDetailPageUrl({
    name: search?.shortName || " ",
    propertyId: property._id,
    propertyName: property?.propertySubTypeDesc || property.propertyName,
    adultCount: search?.adultCount,
    childrenCount: search?.childrenCount,
    petAllowed: search?.petAllowed,
    startDate: search?.startDate || property.checkInDate,
    endDate: search?.endDate || property.checkOutDate,
    eventAllowed: search?.eventAllowed,
    smokeAllowed: search?.smokeAllowed,
    childAllowed: search?.childAllowed,
    FeaturesAndAmenities: search?.FeaturesAndAmenities?.join(",") || "",
    sortBy: search?.sortBy || "highToLow",
    minBedRoom: search?.numberOfBedroom?.toString(),
    minBed: search?.minBed?.toString(),
    minBathRoom: search?.numberOfBathroom?.toString(),
    minPrice: (search?.minPrice ?? 0).toString(),
    maxPrice: (search?.maxprice ?? 0).toString(),
    cancellable: search?.cancellable,
    searchPrice: property?.totalBeforeTaxes ? Math.round(Number(property.totalBeforeTaxes)).toString() : "",
    savedPrice: numericSavePrice || "",
    strikePrice: (property?.originalTotalBeforeTaxes * rate)?.toFixed(2) || "",
    source: "search"
  }), [property._id, property.checkInDate, property.checkOutDate, property?.propertySubTypeDesc, property.propertyName, search?.shortName, search?.adultCount, search?.childrenCount, search?.petAllowed, search?.startDate, search?.endDate, search?.eventAllowed, search?.smokeAllowed, search?.childAllowed, search?.FeaturesAndAmenities, search?.sortBy, search?.numberOfBedroom, search?.minBed, search?.numberOfBathroom, search?.minPrice, search?.maxprice, search?.cancellable, property?.totalBeforeTaxes, numericSavePrice, property?.originalTotalBeforeTaxes, rate]);
  const coverImageUrl = useMemo(() => {
    if (property?.images && property.images.length > 0) {
      const idx = property.images.findIndex(item => item.is_cover_img);
      return idx >= 0 ? property.images[idx].image_full_url : property.images[0].image_full_url;
    }
    return "";
  }, [property.images]);
  const track = () => {
    // Store the exact search page URL so mobile "Back to search" can restore it later.
    // Session storage is per-tab (not shared across tabs).
    const searchUrl = `${window.location.pathname}${window.location.search}`;
    setLastSearchUrl(searchUrl);
    setPropertyPriceForDetailPageEvent(property?.totalBeforeTaxes ? property.totalBeforeTaxes.toString() : "NA");
    if (propertyIndex && isPriceMarker) {
      // trigger mixpanel event on price marker property click
      // do not trigger for dot marker
      void (async () => {
        const selectedFilters = getFiltersForMixpanelEvent(searchQuery);
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client?.CLICK_PROPERTY_ON_SEARCH_RESULT_PAGE({
          ranking: propertyIndex + 1 + (searchQuery?.page || 0) * 21,
          actualPrice: Math.round(property.originalTotalBeforeTaxes),
          city: property?.city,
          state: property?.state,
          country: property?.country,
          dates: searchQuery?.startDate || searchQuery?.endDate ? `${searchQuery?.startDate || ""}, ${searchQuery?.endDate || ""}` : "NA",
          filters: selectedFilters.join(",") || "NA",
          maxPrice: searchQuery?.maxPrice == 1001 ? "NA" : searchQuery?.maxPrice || "NA",
          methodOfSearch: "map area",
          minPrice: searchQuery?.minPrice || "NA",
          noOfGuest: (searchQuery?.adultCount || 0) + (searchQuery?.childrenCount || 0) || "NA",
          numberOfBedroom: searchQuery?.numberOfBedroom || "NA",
          numberOfBeds: searchQuery?.minBed || "NA",
          numberOfBathroom: searchQuery?.numberOfBathroom || "NA",
          propertyId: property._id,
          discountPrice: Math.round(property.totalBeforeTaxes),
          propertyRating: property?.propertyStarSum || "NA",
          savedPrice: Math.round(property.discount),
          shouldDisplayTotalPrice: !!searchQuery?.toggleOn,
          sortBy: searchQuery?.sortBy || "highToLow",
          totalNights: searchQuery?.startDate && searchQuery?.endDate && searchQuery?.noOfDays ? searchQuery?.noOfDays : "NA",
          numberOfDaysBeforeCheckin: searchQuery?.startDate ? diffDays(parseISO(new Date().toJSON().slice(0, 10)), new Date(searchQuery?.startDate)) : 0
        });
      })();
    }
    if (isMultiUnitProperty) {
      const location = [property.city, property.postalName, property.state]?.filter(x => !!x)?.join(", ") || "";
      void (async () => {
        const mixpanelV2Client = (await import("services/mixpanelv2")).mixpanelV2Client;
        mixpanelV2Client?.CLICK_MAP_CARD({
          propertyTitle: property?.propertyName || "",
          location,
          noOfUnits: property?.multiUnitCount || 0,
          displayedPrice: property?.totalBeforeTaxes ? `${formatter(0, 0).format(property.totalBeforeTaxes * rate)}` : "NA"
        });
      })();
    }
    setDetailPageOrigin(pageOrigin);
  };
  return <>
        <div className='relative h-[240px] w-full overflow-hidden rounded-lg'>
          <Link className='w-full' href={propertyUrl} target={isMobile ? "_self" : "_blank"} aria-label='View property details' onClick={() => {
        // Store the exact search page URL so mobile "Back to search" can restore it later.
        // Session storage is per-tab (not shared across tabs).
        const searchUrl = `${window.location.pathname}${window.location.search}`;
        setLastSearchUrl(searchUrl);
      }}>
            {coverImageUrl && <LazyLoadedImage key={`${property._id}-image`} src={coverImageUrl || FALLBACK_LOGO_IMAGE} alt={`${property.propertyName}-map-image`} className='block h-full w-full rounded-lg object-cover text-transparent' loading={"eager"} />}
          </Link>
          {unitIndex && <div className='absolute bottom-[17px] left-1/2 z-[2] -translate-x-1/2 rounded-[40px] bg-[#00000099] px-2 py-1 text-xs font-medium leading-3 text-white'>
              Unit {unitIndex} of {actualPropertyCount}
            </div>}
        </div>
        <div className='relative mt-[15px] lg:mt-2.5'>
          {unitIndex === 1 && <div className='absolute -top-[25px] left-0 z-[4] rounded-[4px] bg-[#E1E3EC] px-2 py-1 text-[10px] font-medium leading-[10px] text-brand-dark'>
              Featured unit
            </div>}
          <TravelAgentPropertyCardBody property={property} propertyUrl={propertyUrl} onLinkClick={track} />
        </div>
      </>;
});
MultiUnitContent.displayName = "MultiUnitContent";
export default MultiUnitContent;